/**
 * Provision billing (TMMPDM-0084, tmm-api 0130): the words for a provisioning failure's
 * `details.reason`, shared by the Setup → Payments confirm and the tenant creation's warning. The
 * server's `message` is never shown (TMM-B3): the words are catalog copy keyed on the reason alone.
 */
import { ApiError } from '@/api/errors';

type Translate = (
  key: string,
  fallback?: string,
  values?: Record<string, string | number>,
) => string;

/** A reason's words; a reason this console doesn't know (or none) reads as pay-api being unavailable. */
export function provisionReasonWords(reason: string | null | undefined, t: Translate): string {
  switch (reason) {
    case 'CONNECTION_EXISTS':
      return t(
        'paymentsSetup.provision.reason.CONNECTION_EXISTS',
        'This tenant already has a different billing connection in pay-api: a connected account, another provider, or the other environment. Someone must remove it in pay-api before billing can be provisioned here.',
      );
    case 'STRIPE_ENVIRONMENT_UNSET':
      return t(
        'paymentsSetup.provision.reason.STRIPE_ENVIRONMENT_UNSET',
        "tmm-api's Stripe environment isn't set, so nothing was sent to pay-api. Ask the platform team.",
      );
    case 'HOUSE_MODE_NOT_ALLOWED':
      return t(
        'paymentsSetup.provision.reason.HOUSE_MODE_NOT_ALLOWED',
        "pay-api doesn't allow tmm to bill on the platform's Stripe account yet. Ask the platform team.",
      );
    case 'HOUSE_MODE_UNAVAILABLE':
      return t(
        'paymentsSetup.provision.reason.HOUSE_MODE_UNAVAILABLE',
        "pay-api doesn't support billing on the platform's Stripe account yet. Ask the platform team.",
      );
    case 'ACTIVATION_REFUSED':
      return t(
        'paymentsSetup.provision.reason.ACTIVATION_REFUSED',
        'pay-api refused to activate the billing connection.',
      );
    default:
      return t(
        'paymentsSetup.provision.reason.PAY_API_UNAVAILABLE',
        "pay-api isn't available right now. Try again; it's safe to repeat.",
      );
  }
}

const KNOWN_REASONS: ReadonlySet<string> = new Set([
  'CONNECTION_EXISTS',
  'STRIPE_ENVIRONMENT_UNSET',
  'HOUSE_MODE_NOT_ALLOWED',
  'HOUSE_MODE_UNAVAILABLE',
  'ACTIVATION_REFUSED',
  'PAY_API_UNAVAILABLE',
]);

/** The failure's `details.reason` (`null` when the server sent none, or not a string). */
export function provisionFailureReason(error: unknown): string | null {
  if (!(error instanceof ApiError)) return null;
  const reason = error.details.backend?.['reason'];
  return typeof reason === 'string' && reason !== '' ? reason : null;
}

/**
 * The reason that words a provisioning failure, or `null` when the failure has none to word (any
 * other error then takes the global `errorCopy`): a reason this console names, or any reason on a
 * 503 `PAY_API_UNAVAILABLE` (pay-api's own, "Try again").
 */
export function provisionWordedReason(error: unknown): string | null {
  const reason = provisionFailureReason(error);
  if (reason !== null && KNOWN_REASONS.has(reason)) return reason;
  if (error instanceof ApiError && String(error.code) === 'PAY_API_UNAVAILABLE') {
    return reason ?? 'PAY_API_UNAVAILABLE';
  }
  return null;
}

/** pay-api's own code on an `ACTIVATION_REFUSED`, for the support copy only (never displayed). */
export function provisionPayApiCode(error: unknown): string | null {
  if (!(error instanceof ApiError)) return null;
  const code = error.details.backend?.['payApiCode'];
  return typeof code === 'string' && code !== '' ? code : null;
}

/**
 * The partner page's one-time warning for a create whose provisioning FAILED (the tenant exists): the
 * reason's words, then where to fix it. The words' own full stop is dropped so the sentence reads on.
 */
export function provisionCreateWarning(reason: string | null, t: Translate): string {
  return t(
    'tenants.create.billingNotProvisioned',
    "The tenant was created, but its billing isn't provisioned yet: {{reason}}. Open Setup → Payments to provision it.",
    { reason: provisionReasonWords(reason, t).replace(/\.$/, '') },
  );
}
